import { getWordpressPostBySlug, getWordpressPostListRelated } from '@src/functions/wordpress'
import { getPost, getPostSlugs, getPostSummaries, serializePost } from '@src/functions/blog'
import BlogPost from '@src/components/BlogPost'
import WordpressPost from '../common/components/WordpressPost'

const BLOG_CATEGORY = '1382'

/**
 * Posts are pre-rendered at build time, not read from disk per request.
 *
 * This is load-bearing on Firebase: the deployed function bundle contains only
 * `.next`, `public`, `node_modules` and config — `src/` is never uploaded. A
 * runtime `fs` read of src/content/blog therefore finds nothing in production,
 * which is exactly the bug this replaces. Rendering during the build puts the
 * finished HTML inside `.next`, which does ship.
 *
 * Archive posts still come from staging.trashwarrior.com, but on first request
 * rather than during the build — see getStaticPaths.
 */
export async function getStaticPaths() {
  // Only local posts are pre-rendered. Archive posts are deliberately left to
  // `fallback: 'blocking'` and rendered on first request: building all of them
  // fires a dozen concurrent requests at the staging WordPress box, which answers
  // with an HTML error page under that load and fails the build.
  return {
    paths: getPostSlugs().map((post) => ({ params: { post } })),
    fallback: 'blocking',
  }
}

export async function getStaticProps(context: any) {
  const post = context.params?.post

  const local = await getPost(post).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn(`[blog] "${post}" failed to parse:`, (error as Error).message)
    return null
  })

  if (local) {
    const summaries = await getPostSummaries()
    const related = summaries.filter((item) => item.slug !== local.slug).slice(0, 5)
    // No `revalidate` here, deliberately. A background regeneration would re-run
    // this in the deployed function, where src/content does not exist — getPost
    // would return null, fall through to WordPress, find nothing, and replace a
    // working post with a 404. Markdown ships with the build, so the build is the
    // only thing that should ever produce it. Publishing a post = a deploy.
    return {
      props: { source: 'local', post: serializePost(local), related: serializePost(related) },
    }
  }

  const postJson = await getWordpressPostBySlug(post)
  const data = Array.isArray(postJson) ? postJson[0] : postJson
  // getWordpressPostBySlug answers with the sentinel string "No post" on failure.
  if (!data || typeof data === 'string') return { notFound: true, revalidate: 60 }

  const relatedJson = await getWordpressPostListRelated(BLOG_CATEGORY)
  return {
    props: { source: 'wordpress', data, related: relatedJson, group: 'blog' },
    revalidate: 3600,
  }
}

function Post({ source, post, data, related, group }: any) {
  if (source === 'local') {
    return <BlogPost post={post} related={related} />
  }
  return <WordpressPost data={data} related={related} group={group} />
}

export default Post
